import type { FormEvent, ReactNode } from 'react';
import { AlertCircle } from '~/components/icons';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  TextInput,
  PasswordInput,
  Button,
  Text,
  Alert,
  Title,
  Reveal,
} from '~/components/ui';

/** Which variant of the credential form to render. */
export type AuthMode = 'sign-in' | 'sign-up';

export interface AuthFormFieldErrors {
  readonly name?: string | undefined;
  readonly email?: string | undefined;
  readonly password?: string | undefined;
}

export interface AuthFormProps {
  /** Renders the sign-in or sign-up variant (title, copy, fields, testids). */
  readonly mode: AuthMode;
  readonly name?: string;
  readonly email?: string;
  readonly password?: string;
  /** Top-level submit error (e.g. BetterAuth `{ error }`), shown in an Alert. */
  readonly error?: string;
  /** Per-field validation messages. */
  readonly fieldErrors?: AuthFormFieldErrors;
  /** Spinner on the credential submit button. */
  readonly loading?: boolean;
  readonly onNameChange?: (value: string) => void;
  readonly onEmailChange?: (value: string) => void;
  readonly onPasswordChange?: (value: string) => void;
  readonly onSubmit?: (e: FormEvent) => void;
  /** Cross-link to the other variant (sign-in ⇄ sign-up). */
  readonly alternateLink?: ReactNode;
  /**
   * Action rendered inside the top-level error Alert — e.g. a "Sign in instead"
   * link shown when a sign-up fails because the email already has an account.
   */
  readonly errorAction?: ReactNode;
  /** "Forgot password?" link, rendered under the password field (sign-in only). */
  readonly forgotPasswordLink?: ReactNode;
}

// Terms/Privacy footer links are intentionally not rendered yet: the public
// marketing/legal pages don't exist at real URLs. The former placeholders
// (https://example.com/*) were removed rather than shipped. Restore a legal
// footer here once the real Terms and Privacy URLs are live.

/**
 * Auth card serving both sign-in and sign-up (controlled via `mode`). Renders
 * an email + password form (plus a name field when signing up) wired through
 * the parent's submit handler and an inline error/validation surface.
 */
export function AuthForm({
  mode,
  name = '',
  email = '',
  password = '',
  error = '',
  fieldErrors = {},
  loading = false,
  onNameChange,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  alternateLink,
  errorAction,
  forgotPasswordLink,
}: AuthFormProps): ReactNode {
  const { t } = useTranslation();
  const isSignUp = mode === 'sign-up';

  const title = isSignUp ? t('auth.signUpTitle') : t('auth.signInTitle');
  const subtitle = isSignUp ? t('auth.signUpSubtitle') : t('auth.signInSubtitle');
  const submitLabel = isSignUp ? t('auth.signUpButton') : t('auth.signInButton');
  const promptText = isSignUp ? t('auth.hasAccountPrompt') : t('auth.noAccountPrompt');

  return (
    <form onSubmit={onSubmit ?? ((e) => e.preventDefault())} noValidate>
      <Reveal variant="fade">
        <Stack gap="xl">
          <div>
            <Title order={1}>{title}</Title>
            <Text c="dimmed" size="sm" mt="xs">
              {subtitle}
            </Text>
          </div>

          {error && (
            <Alert
              icon={<AlertCircle size={16} />}
              color="red"
              variant="light"
              data-testid={`${mode}-error`}
            >
              {error}
              {errorAction ? <div style={{ marginTop: 'var(--sp-xs)' }}>{errorAction}</div> : null}
            </Alert>
          )}

          <Stack gap="sm">
            {isSignUp && (
              <TextInput
                label={t('auth.nameLabel')}
                value={name}
                onChange={
                  onNameChange
                    ? (e) => onNameChange(e.currentTarget.value)
                    : undefined
                }
                error={fieldErrors.name}
                placeholder={t('auth.namePlaceholder')}
                autoComplete="name"
                readOnly={!onNameChange}
                data-testid={`${mode}-name`}
              />
            )}

            <TextInput
              label={t('auth.emailLabel')}
              type="email"
              value={email}
              onChange={
                onEmailChange
                  ? (e) => onEmailChange(e.currentTarget.value)
                  : undefined
              }
              error={fieldErrors.email}
              placeholder={t('auth.emailPlaceholder')}
              autoComplete="email"
              readOnly={!onEmailChange}
              data-testid={`${mode}-email`}
            />

            <PasswordInput
              label={t('auth.passwordLabel')}
              value={password}
              onChange={
                onPasswordChange
                  ? (e) => onPasswordChange(e.currentTarget.value)
                  : undefined
              }
              error={fieldErrors.password}
              description={isSignUp ? t('auth.passwordHint') : undefined}
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              readOnly={!onPasswordChange}
              data-testid={`${mode}-password`}
            />

            {forgotPasswordLink ? (
              <Text ta="right" size="sm">
                {forgotPasswordLink}
              </Text>
            ) : null}
          </Stack>

          <Button
            type="submit"
            size="lg"
            fullWidth
            loading={loading}
            data-testid={`${mode}-submit`}
          >
            {submitLabel}
          </Button>

          <Text ta="center" size="sm">
            {promptText}{' '}
            {alternateLink}
          </Text>
        </Stack>
      </Reveal>
    </form>
  );
}
